import { cond, includes, pipe, T } from 'ramda';
import { createSelector } from 'reselect';

import { optimisticTranslate } from '@i18n';

import { filterArrayByProps, getId, uncurry } from '@framework/cookbook';
import { isContributor, isEditor, isGroupManager, isMember } from '@framework/members';

import { DirtyStateItems, getDirtyStateItemProperties } from '@redux/reducers/DirtyState.duck';

import { MEMBER_STATES } from '@type/members';

export const getIsAddMembersToGroupModalVisible = (state) =>
    state.members?.isAddMembersToGroupModalVisible;
export const getIsGroupFormModalVisible = createSelector(
    (state) => state?.members?.isGroupFormModalVisible,
    getDirtyStateItemProperties(DirtyStateItems.MembersGroupForm),
    (isGroupFormModalVisible, groupFormProperty) =>
        isGroupFormModalVisible && !groupFormProperty.isModalVisible
);
export const getGroupFormModalId = (state) => state?.members?.groupFormModalId;
export const getIsRemoveMemberFromGroupModalVisible = (state) =>
    state?.members?.isRemoveMemberFromGroupModalVisible;
export const getRemoveMemberFromGroupModalId = (state) =>
    state?.members?.removeMemberFromGroupModalId;

const filterArrayByPropsId = (props, fn) => uncurry(pipe(getId, filterArrayByProps(props, fn)));
const getMemberGroups = filterArrayByPropsId(['members'], includes);
const getContributorGroups = (attributes, groups) =>
    groups.filter((group) => attributes.groups.includes(group.id));
const getMemberAndManagedGroups = filterArrayByPropsId(['members', 'managers'], includes);

/**
 * @param {import('@type/members').Member} member
 * @param {import('@type/members').Group[]} groups
 * @type {<U>(member: import('@type/members').Member, groups: import('@type/members').Group[]) => import('@type/members').Group[]}
 */
export const getAvailableGroups = cond([
    [isGroupManager, getMemberAndManagedGroups],
    [isEditor, getMemberGroups],
    [isMember, getMemberGroups],
    [isContributor, getContributorGroups],
    [T, (member, groups) => groups]
]);

export const createMemberNameString = (member) => {
    if (!member) return '';
    const { state, user } = member;
    const fullName = `${user.firstName} ${user.lastName}`;
    if (state === MEMBER_STATES.INACTIVE) {
        return `${fullName} ${optimisticTranslate('(deactivated)')}`;
    }
    return fullName;
};

// mark the member name with 'deactivated' when they are inactive and the room flag is set
export const getMemberName = (member) => createMemberNameString(member);
